import { formatCurrency, formatCurrencyOnBlur } from '@/utils/formatCurrency';
import { Box, Flex, Progress, Text, useMantineTheme } from '@mantine/core';
import { useEffect, useState } from 'react';

type SavingsEstimateCardProps = {
  lowerBound: number;
  upperBound: number;
  totalBillAmount: number;
};

const ANIMATION_DELAY = 400;
const ANIMATION_DURATION = 700;

export const SavingsEstimateCard = ({
  lowerBound,
  upperBound,
  totalBillAmount,
}: SavingsEstimateCardProps) => {
  const theme = useMantineTheme();
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setAnimated(true), ANIMATION_DELAY);
    return () => clearTimeout(timeout);
  }, []);

  const lowerPercent = totalBillAmount > 0 ? (lowerBound / totalBillAmount) * 100 : 0;
  const upperPercent = totalBillAmount > 0 ? (upperBound / totalBillAmount) * 100 : 0;
  const savingsPercent = animated ? upperPercent - lowerPercent : 0;

  return (
    <Box
      p={24}
      sx={{
        border: '1px solid rgba(255, 255, 255, 0.32)',
        borderRadius: 8,
      }}
    >
      <Text
        color="white"
        weight={700}
        size={18}
      >
        Your Savings Estimate
      </Text>
      <Text
        color="minor.1"
        size={16}
        mt={8}
      >
        You&apos;re likely to save anywhere between{' '}
        <Text
          span
          color="white"
          weight={700}
        >
          ${formatCurrencyOnBlur(formatCurrency(lowerBound))}
        </Text>{' '}
        and{' '}
        <Text
          span
          color="white"
          weight={700}
        >
          ${formatCurrencyOnBlur(formatCurrency(upperBound))}
        </Text>
        .
      </Text>

      <Progress
        mt={16}
        size={8}
        radius="xl"
        styles={{
          root: {
            backgroundColor: `${theme.colors.neutral[6]}33`,
          },
          bar: {
            transition: `width ${ANIMATION_DURATION}ms ease-out`,
            '&:last-of-type': {
              borderRadius: theme.radius.xl,
            },
          },
        }}
        sections={[
          { value: lowerPercent, color: 'transparent' },
          { value: savingsPercent, color: theme.colors.secondary[0] },
        ]}
      />

      <Flex
        justify="space-between"
        mt={8}
      >
        <Text
          color="minor.1"
          size={14}
        >
          $0
        </Text>
        <Text
          color="minor.1"
          size={14}
        >
          ${formatCurrency(totalBillAmount)}
        </Text>
      </Flex>
    </Box>
  );
};
